import React, { useState, useEffect, useRef, useMemo } from "react";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/pt-br";
import { useTranslation } from "react-i18next";

/** Moment locale id for month/weekday labels — use per-instance `.locale()`, not global `moment.locale()`, so Safari/mobile matches UI language. */
function resolveMomentLocale(i18nLang) {
    const raw = String(i18nLang || "en").toLowerCase();
    if (raw.startsWith("pt")) return "pt-br";
    if (raw.startsWith("es")) return "es";
    return "en";
}

/**
 * Reusable Calendar Component with Custom Format Support
 * 
 * @param {Object} props
 * @param {string} props.value - The date value (will be parsed based on format)
 * @param {Function} props.onChange - Callback function that receives the date in the specified format
 * @param {string} props.format - Date format: "YYYY-MM-DD" | "MM-DD-YYYY" | "DD-MM-YYYY" (default: "MM-DD-YYYY")
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.id - Input id
 * @param {string} props.name - Input name
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {string} props.min - Minimum date (will be parsed based on format)
 * @param {string} props.max - Maximum date (will be parsed based on format)
 * @param {boolean} props.required - Whether the input is required (a11y)
 * @param {string} props.externalError - Validation message shown under the input (e.g. "required"); also styles border red.
 * @param {number} [props.maxLength] - Forwarded to the text input and shows a character count when set with `id`.
 */
export default function Calendar({
    value = "",
    onChange,
    format = "MM-DD-YYYY",
    placeholder,
    id = "",
    name = "",
    className = "",
    disabled = false,
    min = "",
    max = "",
    required = false,
    externalError = "",
    maxLength,
}) {
    const { t, i18n: i18next } = useTranslation();
    const momentLocale = useMemo(() => resolveMomentLocale(i18next.language), [i18next.language]);
    const [isOpen, setIsOpen] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(moment());
    const [inputValue, setInputValue] = useState("");
    const [isValid, setIsValid] = useState(true);
    const [hasBlurred, setHasBlurred] = useState(false);
    const calendarRef = useRef(null);
    const inputRef = useRef(null);
    const isInternalChangeRef = useRef(false);
    /** iOS/mobile: input blurs before the day-cell click runs and was clearing the value via synchronous blur validation. */
    const blurValidateTimerRef = useRef(null);

    const weekDays = useMemo(() => {
        const start = moment().locale(momentLocale).startOf("week");
        return Array.from({ length: 7 }, (_, i) => start.clone().add(i, "day").format("ddd"));
    }, [momentLocale]);

    const resolvedPlaceholder = placeholder ?? t("cal_placeholder_select");

    // Parse date value based on format (accepts both dashes and slashes)
    const parseDate = (dateString) => {
        if (!dateString) return null;
        // Try formats with dashes and slashes
        const formats = [
            "YYYY-MM-DD", "MM-DD-YYYY", "DD-MM-YYYY",
            "YYYY/MM/DD", "MM/DD/YYYY", "DD/MM/YYYY"
        ];
        for (const fmt of formats) {
            const parsed = moment(dateString, fmt, true);
            if (parsed.isValid()) {
                return parsed;
            }
        }
        return null;
    };

    // Format date according to specified format
    const formatDate = (date) => {
        if (!date) return "";
        return moment(date).format(format);
    };

    // Validate date format (accepts both dashes and slashes)
    const validateDate = (dateString) => {
        if (!dateString || dateString.trim() === "") {
            return { isValid: true, date: null }; // Empty is valid (not required validation)
        }

        // Convert format to also check with slashes
        const formatWithSlashes = format.replace(/-/g, "/");

        // Try to parse with the specified format (both dashes and slashes)
        let parsed = moment(dateString, format, true);
        if (!parsed.isValid()) {
            parsed = moment(dateString, formatWithSlashes, true);
        }
        if (parsed.isValid()) {
            return { isValid: true, date: parsed };
        }

        // Also check other common formats with both dashes and slashes
        const formats = [
            "YYYY-MM-DD", "MM-DD-YYYY", "DD-MM-YYYY",
            "YYYY/MM/DD", "MM/DD/YYYY", "DD/MM/YYYY"
        ];
        for (const fmt of formats) {
            const testParsed = moment(dateString, fmt, true);
            if (testParsed.isValid()) {
                return { isValid: true, date: testParsed };
            }
        }

        return { isValid: false, date: null };
    };

    // Derive selectedDate directly from value prop (no state syncing needed)
    const selectedDate = useMemo(() => {
        if (!value) return null;
        const parsed = parseDate(value);
        return parsed && parsed.isValid() ? parsed : null;
    }, [value, format]);

    // Sync inputValue and currentMonth with value prop (skip if change is internal)
    useEffect(() => {
        if (isInternalChangeRef.current) {
            isInternalChangeRef.current = false;
            return;
        }

        if (value) {
            const parsed = parseDate(value);
            if (parsed && parsed.isValid()) {
                setCurrentMonth(parsed.clone());
                setInputValue(formatDate(parsed));
                setIsValid(true);
            } else {
                setInputValue(value);
            }
        } else {
            setInputValue("");
            setIsValid(true);
        }
    }, [value, format]);

    useEffect(() => {
        return () => {
            if (blurValidateTimerRef.current != null) {
                window.clearTimeout(blurValidateTimerRef.current);
            }
        };
    }, []);

    // Close calendar when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                calendarRef.current &&
                !calendarRef.current.contains(event.target) &&
                inputRef.current &&
                !inputRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            document.addEventListener("touchstart", handleClickOutside, { passive: true });
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, [isOpen]);

    // Get days of the month
    const getDaysInMonth = () => {
        const startOfMonth = currentMonth.clone().startOf("month");
        const endOfMonth = currentMonth.clone().endOf("month");
        const startDate = startOfMonth.clone().startOf("week");
        const endDate = endOfMonth.clone().endOf("week");
        const days = [];
        let currentDate = startDate.clone();

        while (currentDate.isSameOrBefore(endDate, "day")) {
            days.push(currentDate.clone());
            currentDate.add(1, "day");
        }

        return days;
    };

    // Check if date is disabled
    const isDateDisabled = (date) => {
        if (min) {
            const minDate = parseDate(min);
            if (minDate && date.isBefore(minDate, "day")) return true;
        }
        if (max) {
            const maxDate = parseDate(max);
            if (maxDate && date.isAfter(maxDate, "day")) return true;
        }
        return false;
    };

    // Handle date selection
    const handleDateSelect = (date) => {
        if (blurValidateTimerRef.current != null) {
            window.clearTimeout(blurValidateTimerRef.current);
            blurValidateTimerRef.current = null;
        }

        if (!date || !date.isValid()) {
            return;
        }

        if (isDateDisabled(date)) {
            return;
        }

        // Clone the date to ensure we're working with a fresh moment object
        const dateClone = date.clone();

        // Format the date immediately - FORCE this to be the new date
        const formattedDate = formatDate(dateClone);
        // console.log('Formatted date:', formattedDate);

        // FORCE onChange to be called FIRST with the new date - before any state updates
        // This ensures the parent gets the new value immediately
        if (onChange && formattedDate) {
            onChange(formattedDate);
        }

        // Then update internal state
        isInternalChangeRef.current = true;
        setCurrentMonth(dateClone);
        setInputValue(formattedDate);

        // Close calendar
        setIsOpen(false);
    };

    // Navigate months
    const handlePreviousMonth = () => {
        setCurrentMonth(currentMonth.clone().subtract(1, "month"));
    };

    const handleNextMonth = () => {
        setCurrentMonth(currentMonth.clone().add(1, "month"));
    };

    // Navigate to today
    const handleToday = () => {
        const today = moment();
        if (!isDateDisabled(today)) {
            setCurrentMonth(today.clone());
            handleDateSelect(today);
        }
    };

    // Handle manual input change
    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        setHasBlurred(false);
        setIsValid(true); // Clear validation while typing
    };

    // Handle input blur — defer validation so taps on calendar cells commit first on iOS/mobile.
    const handleInputBlur = () => {
        setHasBlurred(true);
        if (blurValidateTimerRef.current != null) {
            window.clearTimeout(blurValidateTimerRef.current);
        }
        blurValidateTimerRef.current = window.setTimeout(() => {
            blurValidateTimerRef.current = null;
            const raw = (inputRef.current?.value ?? "").trim();

            const validation = validateDate(raw);

            if (validation.isValid && validation.date) {
                const formattedDate = formatDate(validation.date);
                isInternalChangeRef.current = true;
                setCurrentMonth(validation.date.clone());
                setInputValue(formattedDate);
                setIsValid(true);
                if (onChange) {
                    onChange(formattedDate);
                }
            } else if (raw === "") {
                isInternalChangeRef.current = true;
                setIsValid(true);
                if (onChange) {
                    onChange("");
                }
            } else {
                setIsValid(false);
            }
        }, 240);
    };

    // Handle Enter key - trigger validation and close calendar
    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            setIsOpen(false); // Close the calendar
            inputRef.current?.blur(); // This will trigger handleInputBlur
        }
    };


    const days = getDaysInMonth();
    const showCharCount = typeof maxLength === "number" && maxLength > 0 && Boolean(id);
    const counterId = showCharCount ? `${id}-counter` : null;
    const ariaDescribedByParts = [];
    if (externalError && id) {
        ariaDescribedByParts.push(`${id}-error`);
    }
    if (counterId) {
        ariaDescribedByParts.push(counterId);
    }

    const parsedErrorOutline = Boolean(externalError) || (hasBlurred && !isValid);
    let dateInputClass =
        "w-full px-4 py-3 pr-10 rounded-xl border bg-white text-slate-900 placeholder:text-slate-400 shadow-sm focus:outline-none focus:ring-2 transition-all duration-200 ";
    if (disabled) {
        dateInputClass +=
            "bg-slate-50 cursor-not-allowed border-slate-200 text-slate-500";
    } else if (parsedErrorOutline) {
        dateInputClass +=
            "cursor-pointer border-red-500 hover:border-red-400 focus:ring-red-500/25";
    } else if (isOpen) {
        dateInputClass +=
            "cursor-pointer border-indigo-500 ring-2 ring-indigo-500/25 focus:ring-indigo-500/20";
    } else {
        dateInputClass +=
            "cursor-pointer border-slate-200 hover:border-indigo-400 focus:border-indigo-500 focus:ring-indigo-500/20";
    }

    return (
        <div className={`relative w-full ${className}`}>
            <div className="relative">
                <input
                    ref={inputRef}
                    type="text"
                    id={id}
                    name={name}
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={resolvedPlaceholder}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    disabled={disabled}
                    required={required}
                    aria-invalid={Boolean(externalError) || (!isValid && hasBlurred)}
                    aria-describedby={ariaDescribedByParts.length > 0 ? ariaDescribedByParts.join(" ") : undefined}
                    maxLength={maxLength}
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    className={dateInputClass}
                />
                <div
                    className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                >
                    <CalendarIcon
                        className={`h-5 w-5 transition-colors duration-200 ${isOpen ? "text-indigo-600" : "text-slate-400"
                            }`}
                    />
                </div>
            </div>

            {/* Error messages */}
            {externalError && (
                <p id={id ? `${id}-error` : undefined} className="mt-1.5 text-sm text-red-600" role="alert">
                    {externalError}
                </p>
            )}
            {!externalError && hasBlurred && !isValid && (
                <p className="mt-1 text-sm text-red-500 flex items-center gap-1" role="alert">
                    <span>{t("alert_fecha_invalida")}</span>
                </p>
            )}
            {showCharCount && (
                <p
                    id={counterId}
                    className="mt-1.5 text-right text-xs tabular-nums text-slate-500"
                    aria-live="polite"
                >
                    {t("te_char_count", {
                        used: inputValue.length,
                        max: maxLength,
                        left: Math.max(0, maxLength - inputValue.length),
                    })}
                </p>
            )}

            {isOpen && !disabled && (
                <div
                    ref={calendarRef}
                    className="absolute z-50 mt-2 bg-white rounded-2xl shadow-xl border border-slate-200 p-4 transform transition-all duration-200 ease-out opacity-100 scale-100 w-full sm:w-auto"
                    style={{ minWidth: "280px" }}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                        <button
                            type="button"
                            onClick={handlePreviousMonth}
                            className="p-2 rounded-xl hover:bg-indigo-50 transition-colors duration-200 group"
                            aria-label="Previous month"
                        >
                            <ChevronLeft className="h-5 w-5 text-slate-600 group-hover:text-indigo-600 transition-colors" />
                        </button>
                        <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-slate-800 capitalize">
                                {currentMonth.clone().locale(momentLocale).format("MMMM YYYY")}
                            </h3>
                        </div>
                        <button
                            type="button"
                            onClick={handleNextMonth}
                            className="p-2 rounded-xl hover:bg-indigo-50 transition-colors duration-200 group"
                            aria-label="Next month"
                        >
                            <ChevronRight className="h-5 w-5 text-slate-600 group-hover:text-indigo-600 transition-colors" />
                        </button>
                    </div>

                    {/* Week days */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                        {weekDays.map((day, index) => (
                            <div
                                key={index}
                                className="text-center text-sm font-semibold text-slate-600 py-2"
                            >
                                {day}
                            </div>
                        ))}
                    </div>

                    {/* Calendar days */}
                    <div className="grid grid-cols-7 gap-1">
                        {days.map((day, index) => {
                            const isCurrentMonth = day.isSame(currentMonth, "month");
                            const isSelected = selectedDate && day.isSame(selectedDate, "day");
                            const isToday = day.isSame(moment(), "day");
                            const isDisabled = isDateDisabled(day);

                            return (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        // console.log('Button clicked, day:', day.format('YYYY-MM-DD'));
                                        const dayClone = day.clone();
                                        handleDateSelect(dayClone);
                                    }}
                                    disabled={isDisabled}
                                    className={`
                                        aspect-square p-2 text-sm rounded-xl transition-all duration-200
                                        ${!isCurrentMonth ? "text-slate-300" : "text-slate-700"}
                                        ${isSelected
                                            ? "bg-indigo-600 text-white font-semibold shadow-md scale-105"
                                            : isToday
                                                ? "bg-violet-50 text-indigo-700 font-semibold border-2 border-indigo-300"
                                                : "hover:bg-slate-100"
                                        }
                                        ${isDisabled
                                            ? "opacity-50 cursor-not-allowed hover:bg-transparent"
                                            : "cursor-pointer hover:scale-105"
                                        }
                                        ${isSelected && isToday ? "ring-2 ring-violet-400" : ""}
                                    `}
                                >
                                    {day.format("D")}
                                </button>
                            );
                        })}
                    </div>

                    {/* Today button */}
                    <div className="mt-4 pt-4 border-t border-slate-200">
                        <button
                            type="button"
                            onClick={handleToday}
                            disabled={isDateDisabled(moment())}
                            className="w-full px-4 py-2.5 text-sm font-semibold text-indigo-700 bg-indigo-50 rounded-xl hover:bg-indigo-100 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {t("cal_today")}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
